import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Action_Status_Enum,
  InputMaybe,
  UpdateActionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';
import { unwrapApprovalGated } from '../unwrapApprovalGated';

export interface UpdateActionTRPCInput {
  Id: string;
  Title: string;
  DateDue: string;
  DateRaised: string;
  Status: Action_Status_Enum;
  Priority?: InputMaybe<number>;
  Description?: InputMaybe<string>;
  ClosedDate?: InputMaybe<string>;
  OriginalTimestamp: string;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  ViewerUserIds?: InputMaybe<string[]>;
  ViewerGroupIds?: InputMaybe<string[]>;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateActionMutation => {
  return {
    updateChildAction: {
      __typename: 'UpdateChildActionOutput',
      affected_rows: 1,
      change_request_id: null,
    },
  };
};

export const useUpdateActionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const changeRequestConfirm = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.action.update.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.actionById.queryKey(),
        });
      },
    }),
  });

  return {
    updateAction: async (
      variables: UpdateActionTRPCInput
    ): Promise<UpdateActionMutation> => {
      const result = await withChangeRequestConfirm({
        input: variables,
        run: (v) => mutation.mutateAsync(v),
        confirm: changeRequestConfirm,
      });

      // A confirmed update resolves as `applied` OR `change-request-created` /
      // `change-request-amended` (a CR was raised) — all success, like
      // useDeleteRiskTRPC. expectApplied would throw on the CR branch and
      // surface a false "Failed to save changes" toast. Echo the input Id on the
      // CR branch (the entity is unchanged).
      return mapTrpcResponseToGraphQL(
        unwrapApprovalGated(result, { Id: variables.Id })
      );
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
